import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Goodies : ajoute / retire automatiquement les cadeaux du panier selon le montant,
 * puis affiche un popup quand un palier est atteint.
 *
 * Les seuils, variantes et disponibilités viennent de snippets/goodies-progress.liquid
 * (balise <script data-goodies-config>) : c'est la seule source de réglages.
 */

const POPUP_KEY_PREFIX = 'goodies-popup:';
const POPUP_IMAGES = {
  tote: 'https://cdn.shopify.com/s/files/1/1071/6135/4572/files/superpetit-accessoires_tote.jpg?v=1790761322',
  mascotte: 'https://cdn.shopify.com/s/files/1/1071/6135/4572/files/superpetit-accessoires_plush.jpg?v=1790761386',
  both: 'https://cdn.shopify.com/s/files/1/1071/6135/4572/files/superpetit-accessoires_tote-plush-set.jpg?v=1790761322',
};
const failedVariants = new Set();
let running = false;
let queued = false;
let timer = 0;

/** @returns {string} */
function root() {
  return window.Shopify?.routes?.root ?? '/';
}

/** @returns {{ base: string, cartUrl: string, tiers: Array<any> } | null} */
function readConfig() {
  const script = document.querySelector('script[data-goodies-config]');
  if (!script) return null;
  try {
    return JSON.parse(script.textContent || '');
  } catch (error) {
    console.warn('[goodies] Configuration illisible', error);
    return null;
  }
}

/**
 * @param {number} cents
 * @param {string} currency
 */
function formatMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(cents / 100);
  } catch (error) {
    return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
  }
}

/** @returns {string[]} */
function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  });
  return Array.from(ids);
}

/**
 * @param {string} key
 * @param {string | null} value
 */
function rememberPopup(key, value) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch (error) {
    /* stockage indisponible : on ignore */
  }
}

/** @param {string} key */
function popupAlreadyShown(key) {
  try {
    return sessionStorage.getItem(key) !== null;
  } catch (error) {
    return false;
  }
}

async function sync() {
  if (running) {
    queued = true;
    return;
  }
  running = true;

  try {
    const config = readConfig();
    if (!config) return;

    const cartResponse = await fetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const cart = await cartResponse.json();
    const total = config.base === 'discounted' ? cart.total_price : cart.original_total_price;

    /** @type {Record<string, number>} */
    const updates = {};
    const unlocked = [];

    for (const tier of config.tiers) {
      const line = cart.items.find((/** @type {any} */ item) => item.variant_id === tier.variantId);
      const current = line ? line.quantity : 0;
      const eligible = tier.available && total >= tier.threshold && !failedVariants.has(tier.variantId);
      const wanted = eligible ? 1 : 0;

      if (wanted === 0) rememberPopup(POPUP_KEY_PREFIX + tier.key, null);

      if (current !== wanted) {
        updates[String(tier.variantId)] = wanted;
        if (wanted === 1 && current === 0) unlocked.push(tier);
      }
    }

    if (Object.keys(updates).length === 0) return;

    /** @type {{ promise: Promise<any>, resolve: Function, reject: Function } | null} */
    let deferred = null;
    try {
      deferred = CartLinesUpdateEvent.createPromise();
      deferred.promise.catch(() => {});
      const event = new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
        promise: deferred.promise,
      });
      // @ts-ignore - marque nos propres événements pour ne pas se re-déclencher
      event.goodies = true;
      document.dispatchEvent(event);
    } catch (error) {
      deferred = null;
    }

    const sectionIds = cartSectionIds();
    const response = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        updates,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();

    if (!response.ok) {
      Object.entries(updates).forEach(([id, quantity]) => {
        if (quantity === 1) failedVariants.add(Number(id));
      });
      console.warn('[goodies] Mise à jour du panier refusée', result?.description || result?.message || result);
      deferred?.reject(new Error(result?.description || result?.message || 'Goodies update failed'));
      return;
    }

    if (deferred) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
        detail: {
          sections: result.sections ?? undefined,
          items: result.items,
          itemCount: result.item_count,
          source: 'goodies-cart',
          didError: false,
        },
      });
    } else {
      await Promise.all(
        sectionIds.map((id) =>
          sectionRenderer
            .renderSection(id, { cache: false, mode: id === 'cart-drawer-section' ? 'hydration' : 'full' })
            .catch(() => {})
        )
      );
    }

    if (unlocked.length > 0) showPopup(config, result, unlocked);
  } catch (error) {
    console.warn('[goodies] Synchronisation impossible', error);
  } finally {
    running = false;
    if (queued) {
      queued = false;
      schedule();
    }
  }
}

/** @param {number} [delay] */
function schedule(delay = 250) {
  window.clearTimeout(timer);
  timer = window.setTimeout(sync, delay);
}

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 * @returns {HTMLElement}
 */
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * Affiche un seul popup : celui du cadeau débloqué, ou un popup commun
 * (visuel tote bag + mascotte) si les deux se débloquent en même temps.
 * @param {any} config
 * @param {any} cart
 * @param {Array<any>} tiers
 */
function showPopup(config, cart, tiers) {
  const fresh = tiers.filter((tier) => !popupAlreadyShown(POPUP_KEY_PREFIX + tier.key));
  if (fresh.length === 0) return;
  fresh.forEach((tier) => rememberPopup(POPUP_KEY_PREFIX + tier.key, '1'));

  const sorted = [...fresh].sort((first, second) => first.threshold - second.threshold);
  const top = sorted[sorted.length - 1];
  const total = config.base === 'discounted' ? cart.total_price : cart.original_total_price;
  const next = config.tiers.find((/** @type {any} */ tier) => tier.available && tier.threshold > top.threshold);

  let popupTier = top;
  if (sorted.length > 1) {
    const names = sorted.map((tier) => tier.name).join(' et ');
    popupTier = {
      key: 'both',
      popupTitle: 'Vos cadeaux sont offerts !',
      addedText: `${names.charAt(0).toUpperCase()}${names.slice(1)} ont été ajoutés à votre panier.`,
    };
  }

  document.querySelector('.goodies-popup')?.remove();
  openPopup(config, popupTier, next, total, cart.currency, () => {}, () => {});
}

/**
 * @param {any} config
 * @param {any} tier
 * @param {any} next
 * @param {number} total
 * @param {string} currency
 * @param {() => void} onClose
 * @param {() => void} onStop
 */
function openPopup(config, tier, next, total, currency, onClose, onStop) {
  let message = tier.addedText;
  if (next) {
    message += ` Encore ${formatMoney(next.threshold - total, currency)} d’achats et ${next.name} s’ajoute aussi.`;
  }

  const dialog = /** @type {HTMLDialogElement} */ (document.createElement('dialog'));
  dialog.className = 'goodies-popup';
  dialog.setAttribute('aria-labelledby', 'goodies-popup-title');

  const closeButton = el('button', 'goodies-popup__close');
  closeButton.setAttribute('type', 'button');
  closeButton.setAttribute('aria-label', 'Fermer');
  closeButton.textContent = '×';
  closeButton.addEventListener('click', () => dialog.close());

  const badge = el('p', 'goodies-popup__badge', 'Cadeau offert');
  // Espace insécable avant « ! » pour qu'il ne se retrouve jamais seul sur une ligne.
  const heading = el('h2', 'goodies-popup__title', tier.popupTitle.replace(/ ([!?:;])/g, ' $1'));
  heading.id = 'goodies-popup-title';
  heading.style.textWrap = 'balance';
  const text = el('p', 'goodies-popup__text', message);

  dialog.append(closeButton, badge);

  const image = POPUP_IMAGES[tier.key];
  if (image) {
    const picture = document.createElement('img');
    picture.className = 'goodies-popup__image';
    picture.src = image;
    picture.alt = '';
    picture.width = 190;
    picture.height = 190;
    dialog.append(picture);
  }

  dialog.append(heading, text);

  if (next) {
    const track = el('div', 'goodies-popup__bar');
    const fill = el('div', 'goodies-popup__bar-fill');
    fill.style.width = `${Math.min(100, Math.round((total / next.threshold) * 100))}%`;
    track.append(fill);
    dialog.append(track);
  }

  const viewCart = el('button', 'goodies-popup__button goodies-popup__button--primary', 'Voir mon panier');
  viewCart.setAttribute('type', 'button');
  viewCart.addEventListener('click', () => {
    onStop();
    dialog.close();
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else if (!window.location.pathname.endsWith('/cart')) window.location.assign(config.cartUrl || '/cart');
  });

  const keepShopping = el('button', 'goodies-popup__button goodies-popup__button--secondary', 'Continuer mes achats');
  keepShopping.setAttribute('type', 'button');
  keepShopping.addEventListener('click', () => {
    onStop();
    dialog.close();
    const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });

  const actions = el('div', 'goodies-popup__actions');
  actions.append(viewCart, keepShopping);
  dialog.append(actions);

  dialog.addEventListener('close', () => {
    dialog.remove();
    onClose();
  });
  document.body.append(dialog);
  dialog.showModal();
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  // @ts-ignore - voir plus haut
  if (event.goodies) return;
  // @ts-ignore - la promesse est fournie par les événements panier du thème
  event.promise?.then(
    () => schedule(),
    () => {}
  );
});

document.addEventListener(StandardEvents.cartDiscountUpdate, (event) => {
  // @ts-ignore
  event.promise?.then(
    () => schedule(),
    () => {}
  );
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) schedule();
});

schedule(0);